import React, { ReactNode, useEffect, useRef, useState } from "react";
import {
  DialogOverlay as ReachDialogOverlay,
  DialogContent as ReachDialogContent,
} from "@reach/dialog";
import {
  Box,
  Button,
  Flex,
  Icon,
  SystemProps,
  Text,
  css,
} from "@storyofams/react-ui";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import SwiperCore from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "react-photo-view/dist/react-photo-view.css";
import "swiper/css";
import { Cross, PlayButton, PlayVideo } from "~components/common/Icons";
import { Slider } from "~components/common/Slider";
import { GallerySlider } from "~components/common/Slider/GallerySlider";
import { FixedRatio } from "../common/FixedRatio";
import { StockLabel } from "./components";
const MotionOverlay = motion(ReachDialogOverlay);
const MotionDialog = motion(ReachDialogContent);

const DialogOverlay = styled(MotionOverlay)<SystemProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  backdrop-filter: blur(7px);
  z-index: ${(p) => p.theme.zIndices.modal};

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, 0.5);
  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    padding: ${(p) => p.theme.space[4]}px ${(p) => p.theme.space[2]}px 92px;
  }
`;

const DialogContent = styled(MotionDialog)<{ children?: ReactNode }>`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 100%;
  background: transparent;
  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    max-width: 774px;
  }
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 100px;
  transition: color 0.2s;
  z-index: 2;
`;

const ContentWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  position: relative;
  margin: 0 auto;
  padding: 40px 16px;
  width: max-content;
  height: max-content;
`;

export type SliderProps = {
  images: {
    type: string;
    src: string;
    preview?: string;
  }[];
  smallImages?: {
    type: string;
    src: string;
  }[];
  inStock?: boolean;
  hideStockLabel?: boolean;
  variant: any;
  product: any;
  videoEmbed?: string;
  videoEmbedPreview?: string;
  embedPreview?: any;
};

const blockedIds = [
  "gid://shopify/Product/6724635131959",
  "gid://shopify/Product/8762003063125",
  "gid://shopify/Product/8819542557013",
];

export const ImageSlider = ({
  images,
  smallImages,
  inStock,
  product,
  hideStockLabel,
  variant,
  videoEmbed,
  videoEmbedPreview,
  embedPreview,
}: SliderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hidden, setHidden] = useState("start");
  const [isOpen, setIsOpen] = useState(false);
  const posthog = usePostHog();
  const { query, replace, isReady } = useRouter();
  const swiperRef = useRef() as any;
  const swiperRefSmall = useRef() as any;
  const isFirstTime = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  useEffect(() => {
    if (variant) {
      if (blockedIds[0] === product.id) {
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else if (!isFirstTime.current && product.id === blockedIds[1]) {
        isFirstTime.current = true;
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else if (!isFirstTime.current && product.id === blockedIds[2]) {
        isFirstTime.current = true;
        setActiveIndex(0);
        swiperRef.current?.swiper.slideTo(0);
      } else {
        const variantImg = variant?.image?.originalSrc?.includes(".png")
          ? variant?.image?.originalSrc.replace(".png", "_1000x1000.png")
          : variant?.image?.originalSrc.replace(".png", "_1000x1000.jpg");
        let index = images.findIndex((img) => img.src === variantImg);
        if (index !== -1 && activeIndex !== index) {
          setActiveIndex(index);
          swiperRef.current?.swiper.slideTo(index);
        }
      }
    }
  }, [variant]);
  const slideToNext = () => swiperRefSmall.current.swiper.slideNext();
  const clearHidden = () => setHidden("");
  useEffect(() => {
    let timeout;
    const imageVariant = product?.variants?.edges?.find(
      (item) =>
        item?.node?.image?.originalSrc ===
        images[activeIndex]?.src?.replace("_800x800", "")
    )?.node?.id;
    if (imageVariant && isReady && !imageVariant.includes(query.variantId)) {
      timeout = setTimeout(
        () =>
          replace(
            {
              pathname: "/products/[handle]",
              query: {
                ...query,
                variantId: imageVariant.replace(/gid:\/\/shopify\/.*\//i, ""),
                handle: query.handle,
              },
            },
            undefined,
            { shallow: true }
          ),
        300
      );
    }
    return () => clearTimeout(timeout);
  }, [activeIndex, isReady]);
  const content = videoEmbed
    ? [{ type: "embed", src: videoEmbed, preview: null }, ...images]
    : images;
  const smallContent = videoEmbed
    ? [
        { type: "embed", src: embedPreview?.filename, preview: null },
        ...smallImages,
      ]
    : smallImages;
  const sliderContent = videoEmbed
    ? [{ type: "embed", src: videoEmbedPreview, preview: null }, ...images]
    : images;
  return (
    <>
      <Box>
        <Box
          width="100%"
          position="relative"
          m="0 auto"
          css={css({
            width: ["100%", "100%", "100%"],
            ".swiper-button-next, .swiper-button-prev": {
              "&:after": {
                content: "unset",
              },
              top: "unset",
              bottom: [1, "50%"],
            },
            ".swiper-container": {
              width: "100%",
              borderRadius: "8px",
            },
            ".product-slider": {
              pb: [1, 2],

              ".swiper-slide": {
                width: "100%!important",
              },
            },
          })}
        >
          <Flex
            zIndex={5}
            bottom={[3, 7]}
            width={"100%"}
            justifyContent={"center"}
            position={"absolute"}
          >
            {content.map(({ src }, i) => (
              <Box
                borderRadius={"full"}
                key={src}
                mr={1}
                height={"10px"}
                width={"10px"}
                bg={activeIndex === i ? "white" : "grey400"}
              ></Box>
            ))}
          </Flex>
          <Box>
            {content?.length >= 1 && (
              <Swiper
                onInit={(core: SwiperCore) => {
                  swiperRef.current = core.el;
                }}
                slidesPerView={1}
                spaceBetween={24}
                mousewheel={false}
                className="product-slider"
                onSlideChange={(e) => {
                  setActiveIndex(e.activeIndex);
                  setIsPlaying(false);
                }}
              >
                {content?.map(({ type, src, preview }, i) => (
                  <SwiperSlide
                    onClick={() => {
                      setIsOpen(true);
                    }}
                    key={src + i}
                  >
                    <FixedRatio
                      ratio={[
                        [260, 260],
                        [640, 640],
                      ]}
                      borderRadius={"8px"}
                      overflow={"hidden"}
                      css={css({
                        video: {
                          borderRadius: "8px",
                        },
                        img: {
                          borderRadius: "8px",
                        },
                      })}
                    >
                      {type === "image" ? (
                        <Image
                          src={src}
                          layout="fill"
                          objectFit="cover"
                          objectPosition="center"
                          priority
                          // blurDataURL={images?.length && images[i]?.src}
                          // placeholder={images?.length ? "blur" : "empty"}
                        />
                      ) : type === "embed" ? (
                        <Box onClick={() => setActiveIndex(0)}>
                          <div dangerouslySetInnerHTML={{ __html: src }}></div>
                        </Box>
                      ) : src.includes("youtu") ? (
                        <>
                          <Box
                            width={"100%"}
                            height={"100%"}
                            display={isPlaying ? "block" : "none"}
                          >
                            <ReactPlayer
                              loop={true}
                              playsinline
                              playing={isPlaying}
                              width={"100%"}
                              onPause={() => setIsPlaying(false)}
                              style={{
                                backgroundImage: "none",
                                objectFit: "cover",
                              }}
                              on
                              height={"100%"}
                              stopOnUnmount={true}
                              url={src}
                            />
                          </Box>
                          <Box display={!isPlaying ? "block" : "none"}>
                            <Image
                              layout="fill"
                              objectFit="cover"
                              src={preview}
                            />
                            <Box
                              width={"80px"}
                              top={0}
                              left={0}
                              right={0}
                              pt="12px"
                              bottom={0}
                              margin={"auto"}
                              pl="27px"
                              borderRadius={"4px"}
                              height={"55px"}
                              bg="white"
                              position={"absolute"}
                              onClick={() => setIsPlaying(true)}
                              cursor={"pointer"}
                            >
                              <Icon icon={PlayButton} fontSize={"30px"} />
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <ReactPlayer
                          loop={true}
                          width={"100%"}
                          style={{
                            backgroundImage: "none",
                            objectFit: "cover",
                          }}
                          muted
                          on
                          playing
                          height={"100%"}
                          stopOnUnmount={true}
                          url={src}
                          playsinline
                        />
                      )}
                    </FixedRatio>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </Box>
          {!hideStockLabel && <StockLabel inStock={inStock} />}
        </Box>
        <Box
          width={"100%"}
          overflow={"clip"}
          display={"flow-root"}
          css={{ "overflow-clip-margin": "content-box 10px;" }}
        >
          <GallerySlider slidesPerView={4}>
            {smallContent.map(({ type, src }, i) => (
              <FixedRatio
                ratio={[[1, 1]]}
                key={src}
                cursor={"pointer"}
                border={i === activeIndex ? "1px solid black" : "none"}
                borderRadius={"8px"}
                width={"100%"}
                position={"relative"}
                css={css({
                  video: {
                    borderRadius: "8px",
                    objectFit: "cover",
                  },
                  img: {
                    borderRadius: "8px",
                  },
                })}
              >
                {type === "image" ? (
                  <Image
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                    src={src}
                    objectFit="cover"
                    layout="fill"
                  />
                ) : type === "embed" ? (
                  <Box
                    borderRadius={"8px"}
                    width={"100%"}
                    height={"100%"}
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                  >
                    <Image src={src} objectFit="cover" layout="fill" />

                    <Icon
                      width={"100%"}
                      mx="auto"
                      left={0}
                      right={0}
                      top={["30%", "34%"]}
                      position={"absolute"}
                      icon={PlayVideo}
                      ml={"3px"}
                      color={"white"}
                      fontSize={["44px", "57px"]}
                    />
                  </Box>
                ) : (
                  <Box
                    borderRadius={"8px"}
                    width={"100%"}
                    height={"100%"}
                    onClick={() => {
                      swiperRef.current?.swiper.slideTo(i);
                    }}
                  >
                    <ReactPlayer
                      url={src}
                      position={"relative"}
                      playing={false}
                      width={"100%"}
                      height={"100%"}
                    />

                    <Icon
                      width={"100%"}
                      mx="auto"
                      left={0}
                      right={0}
                      top={["30%", "34%"]}
                      position={"absolute"}
                      icon={PlayVideo}
                      ml={"3px"}
                      color={"white"}
                      fontSize={["44px", "52px"]}
                    />
                  </Box>
                )}
              </FixedRatio>
            ))}
          </GallerySlider>
        </Box>
      </Box>
      <AnimatePresence>
        {isOpen && (
          <DialogOverlay
            onDismiss={() => setIsOpen(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <DialogContent
              aria-label={"image-modal"}
              initial={{ y: -10 }}
              animate={{ y: 0 }}
              exit={{ y: -10 }}
            >
              <ContentWrapper
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
              >
                <CloseButton
                  variant="unstyled"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close modal"
                >
                  <Icon icon={Cross} fontSize={2.5} color="grey900" />
                </CloseButton>

                <Box
                  width={"800px"}
                  bg="white"
                  className="slider-container"
                  height={["300px", "800px"]}
                  maxHeight={["calc(100vh - 80px)", "100%"]}
                  maxWidth={["calc(100vw - 32px)", "100%"]}
                  overflow="hidden"
                >
                  <Slider initialSlide={activeIndex} perPage={1} isReview>
                    {sliderContent.map(({ src, type }) => (
                      <Box
                        height={["300px", "800px"]}
                        maxHeight={["calc(100vh - 80px)", "100%"]}
                        maxWidth={["calc(100vw - 32px)", "100%"]}
                        key={src}
                        position={"relative"}
                      >
                        {type === "video" ? (
                          <ReactPlayer
                            url={src}
                            height={"100%"}
                            width={"100%"}
                            playing
                            playsinline
                            muted
                            controls
                            config={{
                              youtube: {
                                playerVars: {
                                  controls: true,
                                },
                              },
                            }}
                          />
                        ) : type === "embed" ? (
                          <div dangerouslySetInnerHTML={{ __html: src }}></div>
                        ) : (
                          <Image
                            src={src}
                            objectFit="contain"
                            layout="fill"
                          ></Image>
                        )}
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </ContentWrapper>
            </DialogContent>
          </DialogOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
